import {
	Component,
	computed,
	DestroyRef,
	effect,
	HostBinding,
	inject,
	input,
	Renderer2,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DynamicModule, InputsType } from 'ng-dynamic-component';
import { map, switchMap } from 'rxjs';

import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { SkeletonComponent } from '@valk-nx/components/ui-skeleton/src/lib/skeleton';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { fallbackLanguage, negativeHeader$ } from '@valk-nx/core/lib/core';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';
import { CarouselComponent as SbCarouselComponent } from '@valk-nx/storyblok/components/carousel/src/lib/carousel';
import { CarouselWithAvailabilityWidgetComponent } from '@valk-nx/storyblok/components/carousel-with-availability-widget/src/lib/carousel-with-availability-widget';
import { HeadingGroupComponent } from '@valk-nx/storyblok/components/heading-group/src/lib/heading-group';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { UspWrapperComponent } from '@valk-nx/storyblok/components/usp-wrapper/src/lib/usp-wrapper';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import { MiniHotelSiteData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import {
	HeadingGroupSection,
	ISbStoryBlok,
	UspWrapper,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

type MiniHotelType = 'valk' | 'deals' | 'business';

const Components = {
	carousel: SbCarouselComponent,
	carouselWithAvailabilityWidget: CarouselWithAvailabilityWidgetComponent,
	headingGroup: HeadingGroupComponent,
	richText: RichTextComponent,
	uspwrapper: UspWrapperComponent,
};
@Component({
	selector: 'sb-mini-hotel',
	templateUrl: './mini-hotel.component.html',
	imports: [
		DynamicModule,
		ListGridComponent,
		MapComponent,
		MiniCarouselComponent,
		PersuadeCardComponent,
		RatingComponent,
		SkeletonComponent,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
	],
})
export class MiniHotelComponent extends StoryblokRootDirective {
	@HostBinding('attr.data-test-id') readonly testId = 'mini-hotel';

	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly renderer2 = inject(Renderer2);
	private readonly viewportService = inject(ViewPortService);
	private readonly translate = inject(TranslateService);
	private readonly destroyRef = inject(DestroyRef);
	readonly hotelStore = inject(HotelStore);

	type = input.required<MiniHotelType>();
	carousel = input.required<ISbStoryBlok[]>();
	title = input.required<HeadingGroupSection[]>();
	content = input.required<ISbStoryBlok[]>();
	usps = input.required<UspWrapper[]>();

	readonly hotelSlug = signal<string>('');
	readonly hotelData = signal<MiniHotelSiteData | null>(null);

	readonly components: typeof Components = Components;
	readonly currentLanguage = signal<string>(fallbackLanguage);
	readonly isTablet$ = this.viewportService.isTablet$;

	readonly hotelImages = computed(() => {
		const images = this.hotelData()?.images;
		return this.mapImages(images);
	});

	readonly carouselInputs = computed<(InputsType & { component: string })[]>(
		() => {
			negativeHeader$.next(true);
			return (
				this.carousel()?.map((blok) => ({
					...blok,
					component: blok?.['component'] ?? '',
					hotelSlug: this.hotelSlug(),
				})) ?? []
			);
		},
	);

	constructor() {
		super();
		this.currentLanguage.set(
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang(),
		);

		effect(() => {
			const type = this.type();
			if (type) {
				this.loadHotelData();
			}
		});
	}

	private loadHotelData(): void {
		this.sbService.translatedSlugs$
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				map((slugs) => slugs.find((slug) => slug.selected) ?? slugs[0]),
				switchMap((slug) => {
					this.extractRouteParameters(slug.path);
					return this.contentService.getMiniHotelInfo(
						this.hotelSlug(),
						this.type(),
						slug.lang.toLowerCase(),
					);
				}),
			)
			.subscribe((response) => {
				const hotel = response.data;

				this.hotelStore.updatePartialState({
					accommodationTypes: hotel.accommodationTypes,
					occupancyRestrictions: hotel.occupancyRestrictions,
				});
				this.hotelData.set(hotel);
				this.metadataService.setSchemaOrgMiniHotel(
					this.renderer2,
					hotel,
				);
			});
	}

	private extractRouteParameters(path: string): void {
		const pathSegments = path.split('/');
		this.hotelSlug.set(pathSegments.at(-1));
	}

	private mapImages(images: { src: string; alt?: string }[] | undefined) {
		if (!images?.length) {
			return [];
		}

		return images.map((img) => ({
			src: img.src,
			altText: img.alt,
			width: 0,
			height: 0,
		}));
	}
}
